import { bbox as turfBbox } from "@turf/bbox";
import { feature as turfFeature } from "@turf/helpers";
import lodash from "lodash";
import { type LngLatLike, type Map } from "mapbox-gl";


export interface District {
  _id: string;
  code?: string;
  name?: string;
  region?: {
    _id: string;
    code?: string;
    name?: string;
  };
  geoBoundary?:
    | GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
    | GeoJSON.Polygon
    | GeoJSON.MultiPolygon;
}

export interface DistrictFeatureProperties {
  _id: string;
  name?: string;
  code?: string;
  regionName?: string;
  regionCode?: string;
}

type GeoBoundary = NonNullable<District["geoBoundary"]>;

export const toBoundaryFeature = (
  geoBoundary?: GeoBoundary | null,
): GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> | null => {
  if (!geoBoundary) return null;

  if (geoBoundary.type === "Feature") {
    return geoBoundary as GeoJSON.Feature<
      GeoJSON.Polygon | GeoJSON.MultiPolygon
    >;
  }

  if (geoBoundary.type === "Polygon" || geoBoundary.type === "MultiPolygon") {
    return turfFeature(geoBoundary);
  }

  return null;
};

export const buildDistrictsGeoJson = (
  districts: District[],
): GeoJSON.FeatureCollection<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  DistrictFeatureProperties
> => ({
  type: "FeatureCollection",
  features: districts.flatMap((district) => {
    const feature = toBoundaryFeature(district.geoBoundary);
    if (!feature) return [];

    return [
      {
        ...feature,
        properties: {
          _id: district._id,
          name: district.name,
          code: district.code,
          regionName: district.region?.name,
          regionCode: district.region?.code,
        },
      },
    ];
  }),
});

const GHANA_BOUNDS: [LngLatLike, LngLatLike] = [
  [-3.3, 4.5],
  [1.4, 8.7],
];

export const fitDistrictsBounds = (
  map: Map,
  districts: District[],
  searchParams: { region?: string; district?: string },
) => {
  const fit = (bounds: [LngLatLike, LngLatLike], padding = 40) => {
    map.fitBounds(bounds, { padding, duration: 1000 });
  };

  if (searchParams.district) {
    const district = lodash.find(districts, ["_id", searchParams.district]);
    const feature = toBoundaryFeature(district?.geoBoundary);
    if (feature) {
      const [minLng, minLat, maxLng, maxLat] = turfBbox(feature);
      fit([
        [minLng, minLat],
        [maxLng, maxLat],
      ]);
    }
    return;
  }

  if (searchParams.region && districts.length) {
    const collection = buildDistrictsGeoJson(districts);
    if (collection.features.length) {
      const [minLng, minLat, maxLng, maxLat] = turfBbox(collection);
      fit([
        [minLng, minLat],
        [maxLng, maxLat],
      ]);
    }
    return;
  }

  fit(GHANA_BOUNDS, 10);
  map.setMaxBounds(GHANA_BOUNDS);
};

export const findDistrictById = (
  districts: District[],
  id?: string,
): District | undefined => {
  if (!id) return undefined;
  return lodash.find(districts, ["_id", id]);
};
